import { createFileRoute } from '@tanstack/react-router'

import { SectionHeading } from '@/components/ui'
import { useToast } from '@/hooks'
import { useTrialUploadRoom } from '@/modules/account'
import {
  DeleteStatementsDialog,
  STATEMENT_MAX_UPLOAD_SIZE_MB,
  StatementsTable,
  TrialReadLine,
  TrialUploadNote,
  useFetchProject,
  useFetchTransactions,
  useProjectMutations,
  useStatements,
} from '@/modules/projects'
import { type PresignedBlob, UploadForm } from '@/modules/upload'
import { cn } from '@/utils'

export const Route = createFileRoute('/_authenticated/projects/$slug/files')({
  component: ProjectFiles,
})

function ProjectFiles() {
  const { slug } = Route.useParams()
  useFetchProject(slug)
  // Transactions back the nav's Portfolio/Checks gates, so load them on a direct landing.
  useFetchTransactions(slug)

  const { successToast, errorToast } = useToast()
  const { createStatements } = useProjectMutations()
  const hasStatements = useStatements().length > 0
  const trialUploadRoom = useTrialUploadRoom()

  const onUpload = (blobs: PresignedBlob[]) => {
    createStatements(blobs)
      .then(() => successToast('Statements uploaded successfully'))
      .catch((error) => errorToast('Failed to upload statements', error))
  }

  return (
    <div className="min-h-0 space-y-3">
      <DeleteStatementsDialog />

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Upload statements"
          description="Bank and credit card statements, or check images, as PDF."
        />
        <div
          className={cn(
            'flex flex-col gap-4 rounded-lg border bg-white',
            hasStatements ? 'p-3' : 'p-6',
          )}
        >
          <TrialUploadNote />
          <UploadForm
            onSubmit={onUpload}
            maxSize={STATEMENT_MAX_UPLOAD_SIZE_MB}
            maxPages={trialUploadRoom}
            collapsed={hasStatements}
            pageWideDrop
          />
        </div>
      </section>

      <TrialReadLine />
      <StatementsTable />
    </div>
  )
}
